import React, { ComponentPropsWithoutRef } from 'react';
import { clsx } from 'clsx';
import { Dialog } from '@base-ui-components/react/dialog';

import ActionsCancelNormal from '@strava/icons/ActionsCancelNormalXsmall';
import { color as colors } from '@strava/ui-tokens/js';

import { Button } from '../Button/Button';
import { Element } from '../Element/Element';
import { Typography } from '../Typography/Typography';
import {
  ModalContext,
  ModalContextState,
  useModalContext
} from './Modal.context';
import { CssPropsWithVars } from './Modal.types';

import styles from './Modal.module.scss';

// Anatomy
// <Modal>
//   <Modal.HeroImage />
//   <Modal.Content>
//     <Modal.Title />
//     <Modal.Description />
//     <Modal.Body />
//     <Modal.Actions>
//       <Modal.Close />
//     </Modal.Actions>
//   </Modal.Content>
// </Modal>

/**
 * =====================================
 * Modal
 *
 * Compose's Base UI Modal components.
 * This is the root, parent component.
 * =====================================
 * */
export const Modal = ({
  children,
  triggerProps,
  portalProps,
  backdropProps,
  popupProps,
  viewportProps,
  shouldShowCloseButtonIcon = false,
  shouldRenderTriggerButton = true,
  closeButtonLabel,
  scrollStrategy = 'outside',
  zIndex,
  width,
  maxWidth,
  ...rootProps
}: Modal.Props) => {
  const { buttonProps: buttonTriggerProps, ...restTriggerProps } =
    triggerProps || {};
  const {
    className: popupPropsClassName,
    style: popupStyle,
    ...restPopupProps
  } = popupProps ?? {};
  const {
    className: backdropClassName,
    style: backdropStyles,
    ...restBackdropProps
  } = backdropProps ?? {};
  const {
    className: viewportClassName,
    style: viewportStyles,
    ...restViewportProps
  } = viewportProps ?? {};

  const isOutside = scrollStrategy === 'outside';

  const providerValue = React.useMemo(
    () => ({
      scrollStrategy
    }),
    [scrollStrategy]
  );

  return (
    <ModalContext.Provider value={providerValue}>
      <Dialog.Root {...rootProps}>
        {shouldRenderTriggerButton && (
          <Dialog.Trigger
            render={({ children, ...props }) => (
              <Button variant="primary" {...props}>
                {children}
              </Button>
            )}
            {...(buttonTriggerProps as Button.Props)}
            {...restTriggerProps}
          />
        )}
        <Dialog.Portal {...portalProps}>
          <Dialog.Backdrop
            style={
              {
                ...backdropStyles,
                '--z-index-override': zIndex
              } as CssPropsWithVars
            }
            className={clsx(styles.overlay, backdropClassName)}
            {...restBackdropProps}
          />
          <div
            style={
              {
                ...viewportStyles,
                '--z-index-override': zIndex
              } as CssPropsWithVars
            }
            className={clsx(
              styles.viewport,
              isOutside ? styles.viewportOutside : styles.viewportInside,
              viewportClassName
            )}
            {...restViewportProps}
          >
            <Dialog.Popup
              className={clsx(
                styles.popup,
                isOutside ? styles.popupOutside : styles.popupInside,
                popupPropsClassName
              )}
              style={
                {
                  ...popupStyle,
                  '--z-index-override': zIndex,
                  '--ui-modal-width': width,
                  '--ui-modal-max-width': maxWidth
                } as CssPropsWithVars
              }
              {...restPopupProps}
            >
              {shouldShowCloseButtonIcon && (
                <ModalCloseButtonIcon
                  className={styles.closeButton}
                  aria-label={closeButtonLabel || 'Close Modal'}
                />
              )}
              {children}
            </Dialog.Popup>
          </div>
        </Dialog.Portal>
      </Dialog.Root>
    </ModalContext.Provider>
  );
};
Modal.displayName = 'Modal';

export namespace Modal {
  export type ScrollStrategy = ModalContextState['scrollStrategy'];

  export type TriggerProps = Dialog.Trigger.Props & {
    buttonProps?: Omit<Button.Props, 'children'>;
  };
  export type PortalProps = Dialog.Portal.Props;
  export type BackdropProps = Dialog.Backdrop.Props;
  export type PopupProps = Dialog.Popup.Props & {
    /** Apply custom styles. */
    className?: string;
  };

  export type Props = {
    /** The content of the modal. */
    children: React.ReactNode;
    /** Base UI's <Dialog.Trigger /> props. See: [Trigger Arg Table](https://base-ui.com/react/components/dialog#trigger).  */
    triggerProps?: TriggerProps;
    /** Base UI's <Dialog.Portal /> props. See: [Portal Arg Table](https://base-ui.com/react/components/dialog#portal).  */
    portalProps?: PortalProps;
    /** Base UI's <Dialog.Backdrop /> props. See: [Backdrop Arg Table](https://base-ui.com/react/components/dialog#backdrop). */
    backdropProps?: BackdropProps;
    /** Base UI's <Dialog.Popup /> props. See: [Popup Arg Table](https://base-ui.com/react/components/dialog#popup).  */
    popupProps?: PopupProps;
    /** Props applied to the viewport container that wraps the popup. */
    viewportProps?: React.HTMLAttributes<HTMLDivElement>;
    /** Whether to show the close button icon rendered in the top left corner or not. */
    shouldShowCloseButtonIcon?: boolean;
    /** Whether to show the trigger button. When controlling modal state to open it imperatively from another place in your app then usually you want to hide the trigger button. */
    shouldRenderTriggerButton?: boolean;
    /** The aria-label for the close button icon. */
    closeButtonLabel?: string;
    /**
     * Controls modal scroll behavior.
     *
     * Switch between outer container scrollable vs. inner container scrollable. Defaults to `'outside'` container being scrollable.
     * - `'outside'`: The dialog can extend past the bottom edge while the outer container scrolls (default).
     * - `'inside'`: The modal body content scrolls while the modal stays fully on screen.
     *
     * @default 'outside'
     * @example <Modal scrollStrategy="inside" />
     */
    scrollStrategy?: ScrollStrategy;
    /**
     * Custom z-index override.
     *
     * Override the default z-index of the modal. Use this cautiously to avoid z-index conflicts with other UI elements.
     * This value is applied to both the backdrop and the modal popup.
     *
     * @example <Modal zIndex={9999} />
     */
    zIndex?: number;
    /**
     * Baseline modal width.
     *
     * Sets the modal's baseline width while remaining responsive. Accepts CSS unit strings (`px`, `%`, `rem`, `vw`, etc.).
     * The default is `600px`. The modal automatically shrinks when the viewport is too small.
     *
     * @example <Modal width="720px" />
     * @example <Modal width="60vw" />
     * @example <Modal width="80%" /> // % is relative to the containing block
     */
    width?: string;
    /**
     * Maximum width ceiling the modal can grow to.
     *
     * Sets the ceiling the modal can grow to. Accepts CSS unit strings (`px`, `%`, `rem`, `vw`, etc.). Default inherits
     * from the width value (or `600px` if no width is set). Ideal for modals with dynamic content that should grow but stay within bounds.
     *
     * @example <Modal maxWidth="900px" /> // content-first up to 900px
     * @example <Modal width="800px" maxWidth="760px" /> // width clamped to 760px
     */
    maxWidth?: string;
  } & Dialog.Root.Props;

  // sub-component props
  export type ContentProps = ModalContent.Props;
  export type TitleProps = ModalTitle.Props;
  export type BodyProps = ModalBody.Props;
  export type HeroImageProps = ModalHeroImage.Props;
  export type ActionsProps = ModalActions.Props;
  export type CloseProps = ModalClose.Props;
}

/**
 * =====================================
 * Modal Title
 *
 * A heading that labels the dialog.
 * Renders an <h2> element.
 * =====================================
 * */
export const ModalTitle = ({
  typographyProps,
  className,
  refObject,
  ...props
}: ModalTitle.Props) => (
  <Dialog.Title
    ref={refObject}
    className={clsx(styles.modalTitle, className)}
    render={(renderProps) => (
      <Typography
        renderAs="h2"
        textVariant="h6"
        marginTop="none"
        marginBottom="md"
        marginLeft="none"
        {...typographyProps}
        {...(renderProps as Typography.Props)}
      />
    )}
    {...props}
  />
);
ModalTitle.displayName = 'ModalTitle';

export namespace ModalTitle {
  export type Props = Dialog.Title.Props & {
    /** Props to pass to the Typography component. */
    typographyProps?: Typography.Props<'h2'>;
    // Using any to avoid the headache of having to type the component with forwardRef (which doesn't work well
    // with polymorphic components). Will remove when we upgrade to React 19.
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    refObject?: React.RefObject<any>;
  };
}

/**
 * =====================================
 * Modal Body
 *
 * A paragraph with additional information
 * about the dialog. Renders a <div> element.
 * =====================================
 * */
export const ModalContent = ({
  className,
  refObject,
  ...props
}: ModalContent.Props) => (
  <Element
    ref={refObject}
    className={clsx(styles.modalContent, className)}
    renderAs="div"
    {...props}
  />
);
ModalContent.displayName = 'ModalContent';

export namespace ModalContent {
  export type Props = Element.Props & {
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    refObject?: React.RefObject<any>;
  };
}

/**
 * =====================================
 * Modal Body
 *
 * The main content of the modal. When ScrollStrategy 'inside' is applied,
 * then the body will be scrollable.
 * =====================================
 * */
export const ModalBody = ({
  children,
  typographyProps,
  className,
  ...props
}: ModalBody.Props) => {
  const { scrollStrategy } = useModalContext();
  const isInside = scrollStrategy === 'inside';

  return (
    <Dialog.Description
      className={clsx(styles.modalBody, className, {
        [styles.modalBodyInside as string]: isInside
      })}
      render={(renderProps) => (
        <Typography
          textVariant="body2"
          marginBottom="lg"
          renderAs="div"
          lineHeight="md"
          {...typographyProps}
          {...(renderProps as Typography.Props)}
        >
          {children}
        </Typography>
      )}
      {...props}
    />
  );
};
ModalBody.displayName = 'ModalBody';

export namespace ModalBody {
  export type Props = Dialog.Description.Props & {
    /** Props to pass to the Typography component. */
    typographyProps?: Typography.Props;
  };
}

/**
 * =====================================
 * Modal Description
 *
 * A quick summary of the modal. Content is not scrollable.
 * Acts as a sub-header for the modal.
 * =====================================
 * */
export const ModalDescription = ({
  children,
  typographyProps,
  className,
  ...props
}: ModalDescription.Props) => (
  <Dialog.Description
    className={clsx(styles.modalDescription, className)}
    render={(renderProps) => (
      <Typography
        textVariant="body1"
        marginBottom="lg"
        renderAs="p"
        lineHeight="md"
        {...typographyProps}
        {...(renderProps as Typography.Props)}
      >
        {children}
      </Typography>
    )}
    {...props}
  />
);
ModalDescription.displayName = 'ModalDescription';

export namespace ModalDescription {
  export type Props = Dialog.Description.Props & {
    /** Props to pass to the Typography component. */
    typographyProps?: Typography.Props;
  };
}

/**
 * =====================================
 * Modal Hero Image
 * =====================================
 * */
export const ModalHeroImage = ({
  src,
  alt,
  className,
  ...props
}: ModalHeroImage.Props) => (
  <img
    src={src}
    alt={alt}
    className={clsx(styles.heroImage, className)}
    {...props}
  />
);

export namespace ModalHeroImage {
  export type Props = ComponentPropsWithoutRef<'img'> & {
    children?: React.ReactNode;
    /** Override default styling. */
    className?: string;
  };
}

/**
 * =====================================
 * Modal Close
 *
 * A button that closes the dialog.
 * Renders a <button> element.
 * =====================================
 * */
export const ModalClose = ({
  children,
  buttonProps,
  ...rest
}: ModalClose.Props) => (
  <Dialog.Close
    render={(props) => (
      <Button variant="default" {...buttonProps} {...props}>
        {children}
      </Button>
    )}
    {...rest}
  />
);
ModalClose.displayName = 'ModalClose';

export namespace ModalClose {
  export type Props = Dialog.Close.Props & {
    buttonProps?: Omit<Button.Props, 'children'>;
  };
}

/**
 * =====================================
 * Modal Actions
 *
 * A container for action buttons.
 * =====================================
 * */
export const ModalActions = ({
  children,
  align = 'start',
  className,
  ...otherProps
}: ModalActions.Props) => {
  return (
    <Element
      className={clsx(styles.actionButtons, className, {
        [styles.start as string]: align === 'start',
        [styles.end as string]: align === 'end',
        [styles.full as string]: align === 'full',
        [styles.center as string]: align === 'center'
      })}
      {...otherProps}
    >
      {children}
    </Element>
  );
};
ModalActions.displayName = 'ModalActions';

export namespace ModalActions {
  export type Props = Element.Props & {
    /** Render your action buttons here. */
    children: React.ReactNode;
    /** Alignment of action buttons  */
    align?: 'start' | 'end' | 'full' | 'center';
  };
}

/**
 * =====================================
 * Internal Components
 * =====================================
 * */

/**
 * =====================================
 * Modal Close Button Icon
 * =====================================
 * */
const ModalCloseButtonIcon = ({
  className,
  color,
  ...otherProps
}: ModalCloseButtonIcon.Props) => {
  return (
    <>
      <Dialog.Close
        render={(props) => (
          <button
            type="button"
            className={className}
            data-testid="modal-close-button"
            aria-label="Close Modal"
            {...props}
          >
            <ActionsCancelNormal fill={color ?? colors.colorCoreAsphalt} />
          </button>
        )}
        {...otherProps}
      />
    </>
  );
};

namespace ModalCloseButtonIcon {
  export type Props = {
    className?: string;
    color?: string;
  } & ComponentPropsWithoutRef<'button'>;
}

/**
 * =====================================
 * Compound Components
 * =====================================
 * */
Modal.Content = ModalContent;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Description = ModalDescription;
Modal.HeroImage = ModalHeroImage;
Modal.Actions = ModalActions;
Modal.Close = ModalClose;
